import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { WifiIcon, ShieldCheck, X } from "lucide-react";

/**
 * Remote Work Tracking — employee-portal side.
 *
 * Two responsibilities:
 *   1. Mandatory disclosure modal shown once (per DISCLOSURE_VERSION).
 *      Blocks the portal until the employee explicitly accepts.
 *   2. When a remote session is active, ping the backend every
 *      `heartbeat_interval_sec` (default 15 min) with the session_id.
 *      A missed heartbeat older than 20 min → backend auto clock-out.
 *
 * The whole component is a no-op when `remote_tracking_enabled` is false
 * on the company (the default), so non-remote workflows are untouched.
 */
export default function RemoteTrackingGate({ children }) {
  const [status, setStatus] = useState(null); // { remote_tracking_enabled, disclosure_required, ... }
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const heartbeatRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/remote/status");
      setStatus(data);
    } catch (e) {
      // Silently degrade if endpoint is unreachable — never block the portal.
      setStatus({ remote_tracking_enabled: false });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // ---- Heartbeat lifecycle ----------------------------------------------
  useEffect(() => {
    // Clear any existing heartbeat when status changes.
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (!status?.session_active || !status?.session_id) return;
    const intervalMs = (status.heartbeat_interval_sec || 900) * 1000;
    const beat = async () => {
      try {
        await api.post("/remote/heartbeat", { session_id: status.session_id });
      } catch (e) {
        // If session was reaped (404), refresh status once so the UI reflects it.
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 404) {
          toast.warning(detail || "Remote session ended — please log in again");
          await load();
        }
      }
    };
    // Fire once immediately so the first heartbeat lands soon after login,
    // then on the interval.
    beat();
    heartbeatRef.current = setInterval(beat, intervalMs);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [status?.session_active, status?.session_id, status?.heartbeat_interval_sec]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/remote/disclosure/accept");
      toast.success("Remote tracking disclosure accepted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setAccepting(false);
  };

  if (loading) return children;

  const needsDisclosure = status?.remote_tracking_enabled && status?.disclosure_required;
  const sessionActive = status?.remote_tracking_enabled && status?.session_active;

  return (
    <>
      {sessionActive && <ActiveRemoteBanner status={status} />}
      {children}
      {needsDisclosure && <DisclosureModal accepting={accepting} onAccept={accept} />}
    </>
  );
}

function ActiveRemoteBanner({ status }) {
  const started = status.clock_in_at ? new Date(status.clock_in_at) : null;
  const lastHb = status.last_heartbeat_at ? new Date(status.last_heartbeat_at) : null;
  return (
    <div className="bg-ink text-white border-b-2 border-ink px-6 py-3 flex items-center gap-4 flex-wrap" data-testid="remote-session-banner">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-good opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-good" />
        </span>
        <span className="font-mono text-xs uppercase tracking-widest font-black">Remote session active</span>
      </div>
      {status.worksite_name && <span className="font-mono text-[11px] opacity-80">{status.worksite_name}</span>}
      {started && <span className="font-mono text-[11px] opacity-80">Clock-in {started.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
      {lastHb && <span className="font-mono text-[11px] opacity-60">last heartbeat {lastHb.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
      <span className="ml-auto font-mono text-[10px] opacity-60">Sign out to end the session</span>
    </div>
  );
}

function DisclosureModal({ accepting, onAccept }) {
  return (
    <div className="fixed inset-0 bg-ink/80 z-[100] flex items-center justify-center p-4" data-testid="remote-disclosure-modal">
      <div className="bg-white border-2 border-ink shadow-hard-lg max-w-2xl w-full">
        <div className="border-b-2 border-ink p-5 flex items-center gap-3">
          <div className="p-2 border-2 border-ink bg-ink text-white"><WifiIcon className="w-5 h-5" /></div>
          <div>
            <div className="overline">§ REMOTE WORK TRACKING</div>
            <h2 className="font-heading font-black text-2xl">Please read before continuing</h2>
          </div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <p>
            Your employer uses <b>Checkmate Payroll's Remote Work Tracking</b> to log the hours you work from an
            approved online / remote worksite. By continuing to use the portal, you acknowledge and consent to the following:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-[13px] leading-relaxed">
            <li><b>Auto clock-in / out.</b> Signing in from an approved remote worksite starts a paid time entry. Signing out ends it.</li>
            <li><b>15-minute Heartbeat.</b> The portal quietly pings the payroll service every 15 minutes while you're signed in as proof of continuous activity. A missed heartbeat for more than 20 minutes will automatically clock you out.</li>
            <li><b>IP address logging.</b> Your public IP address, approximate city, and country are recorded on every sign-in. Unexpected locations are flagged for your manager to review.</li>
            <li><b>Purpose:</b> These records are used solely to compute hours worked for payroll. They are not sold, shared, or used for any other purpose.</li>
          </ul>
          <p className="text-[12px] font-mono text-muted-foreground border-t-2 border-ink pt-3">
            If you have questions, contact your HR administrator before accepting. You may still use the portal to view pay stubs, HR documents,
            and payroll history — remote clock-tracking only fires when you're actively signed in.
          </p>
        </div>
        <div className="border-t-2 border-ink p-4 flex justify-end gap-3 bg-secondary">
          <button
            data-testid="remote-disclosure-accept"
            disabled={accepting}
            onClick={onAccept}
            className="hard-btn px-6 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
          ><ShieldCheck className="w-4 h-4" /> {accepting ? "Recording consent…" : "I Understand & Accept"}</button>
        </div>
      </div>
    </div>
  );
}
